import { PRODUCTS } from "../../shared/products";
import type { Lang } from "./i18n";

/** 作者的两个视频平台主页：中文版看 B 站，英文版看 YouTube */
export const BILIBILI_SPACE_URL = "https://space.bilibili.com/1954959";
export const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@BigHippo78";

export interface LocalizedText {
  zh: string;
  en: string;
}

function l(zh: string, en: string): LocalizedText {
  return { zh, en };
}

export function pickText(text: LocalizedText, lang: Lang): string {
  return lang === "zh" ? text.zh : text.en;
}

export interface ProductMeta {
  key: string;
  slug: string;
  /** 授权码前缀（KR / SF / …）。站点上同时用作产品角标的两字母标记 */
  prefix: string;
  icon: string;
  title: LocalizedText;
  instrumentScope: LocalizedText;
  /** 缩略图配色 class（见 style.css 的 .piano / .drums …） */
  thumbClass: string;
  tagline: LocalizedText;
  quickstartSummary: LocalizedText;
  advancedFocus: LocalizedText[];
}

/**
 * 免费版 / 完整版的口径（七款软件一致，全站只在这两个常量里写一次）。
 *
 * 2026-10-19 起七款软件都是「免费版 + 完整版」：免费版装好即用，内置演奏者/角色
 * 可以直接跑完从 midi 到动画的完整流程，不需要授权码。网站上因此只强调「免费能用」，
 * 付费只用一句话带过 —— 具体解锁了哪些功能由软件自己的「解锁全部功能」页说明，
 * 网站不披露明细清单。
 */
export const FREE_EDITION_NOTE: LocalizedText = l(
  "免费版装好即可直接使用：内置演奏者直接可用，从 MIDI 到动画的完整流程都能跑；自定义角色等进阶功能需要完整版。",
  "The free edition works out of the box: bundled performers run the whole MIDI-to-animation pipeline, while custom performers and other advanced features need the full version.",
);

export const FULL_EDITION_NOTE: LocalizedText = l(
  "完整版 ¥98 / 份，一次性买断，解锁自定义角色与全部功能。",
  "The full version is $40 USD per app — a one-time purchase that unlocks custom performers and every feature.",
);


const slugMap: Record<string, string> = {
  string_flow: "string-flow",
  key_ripple: "key-ripple",
  fret_dance: "fret-dance",
  beat_bloom: "beat-bloom",
  zheng_drift: "zheng-drift",
  harp_glide: "harp-glide",
  wind_rise: "wind-rise",
};

/** 缩略图配色：每款乐器一个；首页、快速上手等页面共用这份映射 */
const thumbClassMap: Record<string, string> = {
  key_ripple: "piano",
  string_flow: "strings",
  fret_dance: "guitar",
  beat_bloom: "drums",
  zheng_drift: "zheng",
  harp_glide: "harp",
  wind_rise: "wind",
};

const taglineMap: Record<string, LocalizedText> = {
  key_ripple: l(
    "钢琴演奏动画生成，覆盖十指的按键与换位动作。",
    "Piano performance animation generation covering fingering and hand travel.",
  ),
  string_flow: l(
    "弓弦乐演奏动画生成，覆盖左手按弦与运弓动作。",
    "Bowed-string animation generation covering fingering and bowing.",
  ),
  fret_dance: l(
    "吉他/贝斯演奏动画生成，聚焦弹拨动作与指位变化。",
    "Guitar and bass animation generation focused on plucking and finger positions.",
  ),
  beat_bloom: l(
    "鼓组演奏动画生成，覆盖四肢节奏与鼓组联动表现。",
    "Drum performance animation generation for limbs and kit coordination.",
  ),
  zheng_drift: l(
    "古筝演奏动画生成，支持民族乐器专属动作逻辑。",
    "Guzheng animation generation with instrument-specific motion logic.",
  ),
  harp_glide: l(
    "竖琴演奏动画生成，包含手部与踏板联动。",
    "Harp performance animation generation including hand and pedal linkage.",
  ),
  wind_rise: l(
    "管乐演奏动画生成，强调指法、呼吸与口型变化。",
    "Wind-instrument animation generation emphasizing fingering, breathing, and embouchure.",
  ),
};

const instrumentScopeMap: Record<string, LocalizedText> = {
  key_ripple: l("服务乐器：钢琴。", "Instruments served: piano."),
  string_flow: l(
    "服务乐器：弦乐（小提琴 / 中提琴 / 大提琴）。",
    "Instruments served: strings (violin, viola, cello).",
  ),
  fret_dance: l(
    "服务乐器：吉他（古典吉他 / 电吉他 / 贝斯）。",
    "Instruments served: guitar family (classical guitar, electric guitar, bass).",
  ),
  beat_bloom: l(
    "服务乐器：打击乐（如架子鼓）。",
    "Instruments served: percussion (such as drum kits).",
  ),
  zheng_drift: l("服务乐器：古筝。", "Instruments served: guzheng."),
  harp_glide: l("服务乐器：竖琴。", "Instruments served: harp."),
  wind_rise: l(
    "服务乐器：管乐器（覆盖绝大多数一次仅演奏一个音的乐器）。",
    "Instruments served: wind instruments (covering most monophonic instruments).",
  ),
};

const quickstartSummaryMap: Record<string, LocalizedText> = {
  key_ripple: l(
    "打开预设工程，导入 MIDI，生成数据后加载即可出效果。",
    "Open the preset project, import MIDI, generate data, and load for immediate results.",
  ),
  string_flow: l(
    "使用预设角色和工程模板，几步完成弦乐动画预览。",
    "Use preset performer and templates to preview string animation in a few steps.",
  ),
  fret_dance: l(
    "直接在预设工程里加载生成结果，快速看到弹拨成品。",
    "Load generated results directly in the preset project to quickly preview plucked performance.",
  ),
  beat_bloom: l(
    "用预设鼓手与鼓组模板，快速跑通打击乐全流程。",
    "Run the full percussion workflow quickly using preset drummer and drum-kit templates.",
  ),
  zheng_drift: l(
    "通过预设古筝工程，快速生成并加载双手演奏动画。",
    "Use preset guzheng projects to quickly generate and load two-hand performance animation.",
  ),
  harp_glide: l(
    "用预设竖琴工程快速展示手部与琴体联动。",
    "Use preset harp projects to quickly show hand-and-body linkage.",
  ),
  wind_rise: l(
    "在预设管乐工程中快速完成演奏数据加载与播放。",
    "Quickly load and preview performance data in preset wind-instrument projects.",
  ),
};

const advancedFocusMap: Record<string, LocalizedText[]> = {
  key_ripple: [
    l("自定义钢琴角色骨骼检查", "Custom piano rig bone inspection"),
    l("手部约束绑定", "Hand constraint binding"),
    l("演奏姿态基线校准", "Performance pose baseline calibration"),
    l("角色数据导出", "Performer data export"),
  ],
  string_flow: [
    l("琴体与角色比例校准", "Instrument-performer proportion calibration"),
    l("左手/运弓约束修正", "Left-hand and bowing constraint adjustment"),
    l("基准持琴姿势设定", "Baseline holding posture setup"),
    l("角色数据导出", "Performer data export"),
  ],
  fret_dance: [
    l("指板尺度与手型调整", "Fretboard scale and hand-shape adjustment"),
    l("左右手约束绑定", "Left/right hand constraint binding"),
    l("基准演奏姿势设定", "Baseline playing posture setup"),
    l("角色数据导出", "Performer data export"),
  ],
  beat_bloom: [
    l("鼓组与角色相对位置校准", "Drum-kit and performer alignment"),
    l("四肢控制器约束检查", "Limb controller constraint checks"),
    l("基准击打姿势设定", "Baseline drumming pose setup"),
    l("角色数据导出", "Performer data export"),
  ],
  zheng_drift: [
    l("古筝与角色高度对齐", "Guzheng and performer height alignment"),
    l(
      "双手约束与触弦姿态调整",
      "Two-hand constraints and touch posture tuning",
    ),
    l("基准坐姿设定", "Baseline seated pose setup"),
    l("角色数据导出", "Performer data export"),
  ],
  harp_glide: [
    l("竖琴角度与角色比例调整", "Harp angle and performer proportion tuning"),
    l("手脚约束绑定", "Hand and foot constraint binding"),
    l("基准演奏姿势设定", "Baseline performance posture setup"),
    l("角色数据导出", "Performer data export"),
  ],
  wind_rise: [
    l("乐器与嘴部对齐", "Instrument and mouth alignment"),
    l("手指约束绑定", "Finger constraint binding"),
    l("基准吹奏姿势与口型设定", "Baseline blowing pose and embouchure setup"),
    l("角色数据导出", "Performer data export"),
  ],
};

export const PRODUCT_META: ProductMeta[] = PRODUCTS.map((p) => ({
  key: p.key,
  slug: slugMap[p.key],
  prefix: p.prefix,
  icon: p.emoji,
  title: l(p.nameZh || p.nameEn, p.nameEn || p.nameZh),
  instrumentScope: instrumentScopeMap[p.key],
  thumbClass: thumbClassMap[p.key] ?? "piano",
  tagline: taglineMap[p.key],
  quickstartSummary: quickstartSummaryMap[p.key],
  advancedFocus: advancedFocusMap[p.key],
}));

export function byKey(key: string): ProductMeta | undefined {
  return PRODUCT_META.find((p) => p.key === key);
}
